import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LevelChangeOut,
  ackLevelChange,
  getUnreadLevelChanges
} from '@/api/alianca';
import { AliancaSubiu } from './AliancaSubiu';
import { AliancaRisco } from './AliancaRisco';

/**
 * Renderiza Momentos (Subiu / Risco) como overlay full-screen ao detectar
 * `LevelChange`s não lidos do cliente atual.
 *
 * Estratégia:
 *  1. No mount, GET /alianca/level-changes/unread.
 *  2. Filtra `direction === 'same'` (sem signal).
 *  3. Renderiza o mais recente do queue como overlay full-screen:
 *     - `up`   → AliancaSubiu (celebração)
 *     - `down` → AliancaRisco (queda + caminho de recuperação)
 *  4. Cliente dismissa → POST /ack → tira do queue → próximo (se houver).
 *
 * Stub de push notification (V2 = FCM/APNs real). Notion §5.3 / §8.4.
 *
 * NB: nome do componente preservado por compat com AppLayout — antes era um
 * toast pequeno; agora é overlay full-screen. Visualmente é uma reescrita,
 * mas a API (mount-and-forget) é a mesma.
 */
export function LevelChangeToast() {
  const navigate = useNavigate();
  const [changes, setChanges] = useState<LevelChangeOut[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    getUnreadLevelChanges()
      .then((list) =>
        setChanges(list.filter((c) => c.direction !== 'same'))
      )
      .catch(() => {
        // user sem auth ou sem aliança ativa — silencioso
      });
  }, []);

  // Próximo evento a renderizar
  const current = changes.find((c) => !dismissed.has(c.id));
  if (!current) return null;

  async function handleDismiss() {
    if (!current) return;
    const id = current.id;
    setDismissed((prev) => new Set(prev).add(id));
    try {
      await ackLevelChange(id);
    } catch {
      // best-effort — fica não-acked, mas dispensado nessa sessão
    }
  }

  function handleFindStation() {
    void handleDismiss();
    navigate('/');
  }

  if (current.direction === 'up') {
    return (
      <AliancaSubiu
        fromNivel={current.from_nivel}
        toNivel={current.to_nivel}
        onDismiss={handleDismiss}
      />
    );
  }

  // direction === 'down'
  return (
    <AliancaRisco
      fromNivel={current.from_nivel}
      toNivel={current.to_nivel}
      onFindStation={handleFindStation}
      onDismiss={handleDismiss}
    />
  );
}
